import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Page identifiers that map to routes
export type PageId = 'dashboard' | 'studios' | 'users' | 'subscriptions' | 'discounts' | 'leads' | 'analytics' | 'guide' | 'playbook' | 'roles' | 'settings';

export interface RolePermission {
  pages: PageId[];
  defaultPage: string;
}

export interface RolePermissions {
  [role: string]: RolePermission;
}

interface PermissionsContextType {
  permissions: RolePermissions | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  hasAccess: (role: string, pageId: PageId) => boolean;
  getDefaultPage: (role: string) => string;
  getAllowedPages: (role: string) => PageId[];
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

const API_URL = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';

// Default permissions used as fallback
const DEFAULT_PERMISSIONS: RolePermissions = {
  admin: {
    pages: ['dashboard', 'studios', 'users', 'subscriptions', 'discounts', 'leads', 'analytics', 'guide', 'playbook', 'roles', 'settings'],
    defaultPage: '/'
  },
  marketing: {
    pages: ['leads', 'playbook'],
    defaultPage: '/leads'
  },
  pending: {
    pages: [],  // No access until approved
    defaultPage: '/'
  }
};

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const [permissions, setPermissions] = useState<RolePermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/admin/role-permissions`);

      if (!response.ok) {
        throw new Error(`Failed to fetch permissions: ${response.status}`);
      }

      const data = await response.json();
      setPermissions(data.permissions || DEFAULT_PERMISSIONS);
    } catch (err: any) {
      console.error('Failed to fetch role permissions:', err);
      setError(err.message);
      // Use defaults on error
      setPermissions(DEFAULT_PERMISSIONS);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const hasAccess = (role: string, pageId: PageId): boolean => {
    const rolePerms = permissions?.[role];
    if (!rolePerms) return false;
    return rolePerms.pages.includes(pageId);
  };

  const getDefaultPage = (role: string): string => {
    const rolePerms = permissions?.[role];
    return rolePerms?.defaultPage || '/';
  };

  const getAllowedPages = (role: string): PageId[] => {
    const rolePerms = permissions?.[role];
    return rolePerms?.pages || [];
  };

  return (
    <PermissionsContext.Provider value={{
      permissions,
      isLoading,
      error,
      refetch: fetchPermissions,
      hasAccess,
      getDefaultPage,
      getAllowedPages
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
